import { useRef, useState } from 'react'

import { decrementAttr, incrementAttr } from '@utils/postAttr'
import { PostDisplayParser } from '@utils/postDisplayParser'
import useSessionState from '@hooks/useSessionState'
import axios from '@utils/webRequests'

class PostIndex {
  // Holds data for getting the main post in the postState.posts quickly
  constructor(posts) {
    this.index = {}
    if (posts && Array.isArray(posts)) {
      posts.forEach((post, i) => this.add(post.uuid, i))
    }
  }

  add(uuid, postIndex) {
    if (this.index[uuid]) {
      this.index[uuid].push(postIndex)
    } else {
      this.index[uuid] = [postIndex]
    }
  }

  get(uuid, posts=[]) {
    return this.index[uuid]?.map?.((i) => posts[i])
  }
}

const postStateInterface = {
  posts: [],
  postHeights: [],
  index: new PostIndex(),
}

const useFeed = ({ getPostsUrl, feedAccessor, postStateCallback, postStats }) => {
  const { insertStat, insertStats, getStat } = postStats
  const [postsState, setPostsState, postsStateRef] = useSessionState(feedAccessor, structuredClone(postStateInterface), (postStateCallback || undefined))
  const postHeights = postsState?.postHeights || postStateInterface.postHeights
  const posts = postsState?.posts || postStateInterface.posts
  const PostIndexRef = useRef(new PostIndex(posts))
  const [postsLoaded, setPostsLoaded] = useState(false)
  const [noMorePosts, setNoMorePosts] = useState(false)
  const postsEmpty = postsLoaded && posts?.length === 0
  const firstRender = useRef(true)

  const setPostsAndHeights = (posts=[]) => {
    if (!Array.isArray(posts)) return;
    const newPosts = []
    const newHeights =  []
    const newIndex = new PostIndex()

    // Collect valid new posts with display into accumulator
    posts.forEach((post) => {
      // If no post display, attempt to set post display
      if (!post?.display) {
        try { PostDisplayParser.insertDisplayAndDimensions({ post }) }
        catch (err) { console.log(err) }
      }

      // If post has display, or display was set without error, add it to data
      if (post?.display) {
        // Add to posts array
        newPosts.push(post)
        // Add to postHeights
        newHeights.push(post?.display?.totalHeightPx || 0)
        // Add to posts index
        newIndex.add(post?.uuid, newPosts.length - 1)
      }
    })

    // Set to State
    setPostsState({ posts: newPosts, postHeights: newHeights })
    PostIndexRef.current = newIndex
  }

  function getPosts(options = { offset: 0 }) {
    setPostsLoaded(false)
    return axios.get(getPostsUrl, { params: { offset: options?.offset || 0 } })
  }

  const getInitialPosts = () => getPosts()
    .then((res) => handleInitialPosts(res.data))
    .catch(console.log)
    
  const handleInitialPosts = (postData) => {
    // normalize to array, in case where post object returned
    let newPosts = normalizeToArray(postData)
    // add post display + dimensions and set
    setPostsAndHeights(newPosts)
    // insert post stats to global context
    if (insertStats) insertGlobalStats(newPosts)
    // state cleanup
    setPostsLoaded(true)
    setNoMorePosts(postData.length < 25)
    firstRender.current = false
  }

  const getMorePosts = () => getPosts({ offset: (postsStateRef?.current?.posts?.length || 0) })
    .then((res) => handleMorePosts(res.data))
    .catch(console.log)

  const handleMorePosts = (postData) => {
    // normalize to array (in case where post object returned), and remove posts that are already rendered
    let newPosts = filterExistingPosts(normalizeToArray(postData))
    // add post display + dimensions and set
    const oldPosts = postsStateRef?.current?.posts || posts
    setPostsAndHeights(oldPosts.concat(newPosts))
    // insert post stats to global context
    if (insertStats) insertGlobalStats(newPosts)
    // state cleanup
    setPostsLoaded(true)
    if (postData?.length < 25) setNoMorePosts(true)
  }


  const normalizeToArray = (objectOrArray) => {
    if (objectOrArray && !Array.isArray(objectOrArray)) {
      return [objectOrArray]
    } else {
      return objectOrArray
    }
  }

  const filterExistingPosts = (posts) => (
    posts?.filter?.((post) => (
      !PostIndexRef?.current?.get(post?.uuid, postsStateRef?.current?.posts)
    ))
  )

  const setLikeToState = (post) => {
    let displayAccessor = PostDisplayParser.displayAccessor(post.displayType)
    let displayPost

    // // Get the main posts with the child post in display and iterate actions
    PostIndexRef.current?.get?.(post?.mainPostId, posts)?.forEach((oldPost) => {
      if (!oldPost?.display?.data || !displayAccessor) return oldPost;
      displayPost = oldPost?.display?.data?.[displayAccessor].post
      updateToGlobalStats(displayPost)
      incrementAttr(displayPost, post.uuid, 'likeCount', (p) => p.currentUserLiked = 1)
      if (insertStat) insertStat(displayPost);
      return oldPost
    })

    setPostsAndHeights(posts)
  }

  const setUnlikeToState = (post) => {
    let displayAccessor = PostDisplayParser.displayAccessor(post.displayType)
    let displayPost

    PostIndexRef?.current?.get?.(post?.mainPostId, posts)?.forEach((oldPost) => {
      if (!oldPost?.display?.data || !displayAccessor) return oldPost;
      displayPost = oldPost?.display?.data?.[displayAccessor].post
      updateToGlobalStats(displayPost)
      decrementAttr(displayPost, post.uuid, 'likeCount', (p) => p.currentUserLiked = 0)
      if (insertStat) insertStat(displayPost);
      return oldPost
    })

    setPostsAndHeights(posts)
  }

  const setRepostToState = (post) => {
    let displayAccessor = PostDisplayParser.displayAccessor(post.displayType)
    let displayPost

    PostIndexRef?.current?.get?.(post?.mainPostId, posts)?.forEach((oldPost) => {
      if (!oldPost?.display?.data || !displayAccessor) return oldPost
      displayPost = oldPost?.display?.data?.[displayAccessor].post
      updateToGlobalStats(displayPost)
      incrementAttr(displayPost, post.uuid, 'repostCount')
      if (insertStat) insertStat(displayPost);
      return oldPost
    })

    setPostsAndHeights(posts)
  }

  const newReplyAfterCreate = (newReply, mainPostId) => {
    if (!newReply?.parent) return;
    // Because the newReply doesn't have the display post, we can't actually get a display accessor from it.
    const repliedToUuid = newReply?.parent?.uuid

    // Get the main post, and iterate through child objects until we find the right one to increment
    PostIndexRef?.current?.get?.(mainPostId, posts)?.forEach((oldPost) => {
      if (!oldPost?.display?.data) return oldPost
      Object.values(oldPost.display.data).forEach((displayPost) => {
        if (displayPost?.post?.uuid === repliedToUuid) {
          updateToGlobalStats(displayPost?.post)
          incrementAttr(displayPost?.post, repliedToUuid, 'replyCount')
          if (insertStat) insertStat(displayPost?.post);
        }
      })
      return oldPost
    })

    // Set new posts
    setPostsAndHeights(posts)
  }

  const afterPostDelete = (deletedPost) => {
    const newPosts = posts.filter((oldPost) => {
      if (
        oldPost.uuid === deletedPost.uuid
        || oldPost?.parent?.uuid === deletedPost.uuid
        || oldPost?.original?.uuid === deletedPost.uuid
      ) {
        return null
      } else {
        return oldPost
      }
    })
    setPostsAndHeights(newPosts)
  }

  const addPostToFeedStart = (newPost) => {
    if (newPost && newPost?.uuid && newPost.user) {
      setPostsAndHeights([newPost, ...(postsStateRef?.current?.posts || posts)])
    }
  }

  const updateToGlobalStats = (post) => {
    Object.assign(post, (getStat(post?.uuid) || {}))
  }

  const insertGlobalStats = (postsWithDisplay) => {
    let displayPosts = []
    postsWithDisplay.forEach((post) => {
      if (!post?.display?.data) return;
      const newPosts = Object.values(post?.display?.data).filter((displayPost) => {
        return displayPost.post
      })
      displayPosts.push(...newPosts)
    })
    displayPosts = displayPosts.map(({ post }) => post)
    insertStats(displayPosts)
  }

  return {
    postsState,
    postHeights,
    posts,
    setPosts: setPostsAndHeights,
    setPostsAndHeights,
    postsLoaded: posts?.length > 0 || postsLoaded,
    setPostsLoaded,
    noMorePosts,
    setNoMorePosts,
    postsEmpty,

    getPosts,
    getInitialPosts,
    handleInitialPosts,
    getMorePosts,
    handleMorePosts,
    addPostToFeedStart,

    incrementAttr,
    decrementAttr,

    setLikeToState,
    setUnlikeToState,
    setRepostToState,
    newReplyAfterCreate,
    afterPostDelete,

    firstRender,
    feedAccessor,
  }
}

export { postStateInterface }
export default useFeed
